// Visual aids for calibration: a reference box (10 cm cube, or a Duplo brick
// when the target object is Duplo), a 1 cm grid on the ground plane, and axes
// at the origin. The box can be snapped to a perch (Phase 5 gate). Hidden in
// normal kiosk operation.
import * as THREE from 'three';
import { config } from '../config.js';
import { storage } from '../util/storage.js';

const KEY = 'mw.aids.visible';
const SHAPE_KEY = 'mw.aids.shape';
const UP = new THREE.Vector3(0, 1, 0);

// Sizes in metres: width (x), height (y), depth (z). Duplo stud pitch is
// 16 mm, brick height 19.2 mm, so a 2x2 is 32 x 19.2 x 32.
export const SHAPES = {
  cube10: { label: 'cube 10 cm', size: [config.calibration.cubeSize, config.calibration.cubeSize, config.calibration.cubeSize] },
  duplo2x2: { label: 'Duplo 2x2', size: [0.032, 0.0192, 0.032] },
  duplo2x4: { label: 'Duplo 2x4', size: [0.064, 0.0192, 0.032] },
  duploStud: { label: 'Duplo stud pitch 16 mm', size: [0.016, 0.0045, 0.016] },
};

export function createAids(scene) {
  const group = new THREE.Group();
  group.name = 'calibration-aids';

  const box = new THREE.Group();
  const lineMat = new THREE.LineBasicMaterial({ color: 0x00ffcc, depthTest: false });
  const faceMat = new THREE.MeshBasicMaterial({ color: 0x00ffcc, transparent: true, opacity: 0.08, depthWrite: false });
  let edges = null;
  let faces = null;
  let shape = storage.get(SHAPE_KEY) ?? 'cube10';
  function buildBox() {
    if (edges) box.remove(edges, faces);
    const [w, h, d] = (SHAPES[shape] ?? SHAPES.cube10).size;
    const geo = new THREE.BoxGeometry(w, h, d);
    edges = new THREE.LineSegments(new THREE.EdgesGeometry(geo), lineMat);
    faces = new THREE.Mesh(geo, faceMat);
    edges.position.y = h / 2; // base of the box on the reference plane
    faces.position.y = h / 2;
    box.add(edges, faces);
  }
  buildBox();

  const grid = new THREE.GridHelper(
    config.calibration.gridSize,
    Math.round(config.calibration.gridSize / config.calibration.gridCell),
    0xffcc00,
    0x66aa55,
  );
  grid.material.transparent = true;
  grid.material.opacity = 0.6;
  grid.material.depthTest = false;

  const axes = new THREE.AxesHelper(config.calibration.cubeSize / 2);
  axes.material.depthTest = false;

  group.add(box, grid, axes);
  scene.add(group);

  group.visible = storage.get(KEY) ?? true;

  return {
    group,
    cube: box,
    get visible() {
      return group.visible;
    },
    setVisible(v) {
      group.visible = v;
      storage.set(KEY, v);
    },
    get shape() {
      return shape;
    },
    setShape(name) {
      if (!SHAPES[name]) return;
      shape = name;
      storage.set(SHAPE_KEY, name);
      buildBox();
    },
    /** Put the box's base at a perch, oriented to its normal. */
    snapCube(perch) {
      box.position.fromArray(perch.position);
      box.quaternion.setFromUnitVectors(UP, new THREE.Vector3().fromArray(perch.normal).normalize());
    },
    resetCube() {
      box.position.set(0, 0, 0);
      box.quaternion.identity();
    },
  };
}
